import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { cn } from "@/lib/utils";
function DesktopNavigation() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="31015" className="hidden lg:flex items-center gap-8">
        {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-3 w-20 bg-foreground/10 animate-pulse rounded-md" />)}
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden lg:flex">
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              {}
              <Link to={item.href || "#"} className="group relative text-xs font-mono font-medium uppercase tracking-widest text-foreground transition-colors hover:text-foreground/80 hover:bg-transparent focus:bg-transparent">
                {item.label}
                <span className="absolute -bottom-1 left-0 h-[1px] w-0 bg-foreground transition-all duration-300 ease-out group-hover:w-full" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileNavigation({
  isOpen,
  setIsOpen
}: {
  isOpen: boolean;
  setIsOpen: (v: boolean) => void;
}) {
  const {
    menuItems
  } = useMenu();
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-40 flex flex-col bg-background/95 backdrop-blur-xl pt-24 px-6 lg:hidden animate-in fade-in slide-in-from-top-5 duration-200">
      <nav className="flex flex-col gap-6 items-center">
        {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="text-xl font-mono font-medium uppercase tracking-widest text-foreground hover:text-foreground/80 transition-colors" onClick={() => setIsOpen(false)}>
            {item.label}
          </Link>)}
        {}
        <Button variant="ghost" className="mt-8 text-sm font-mono uppercase tracking-widest w-full max-w-xs bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg shadow-none" asChild>
          <Link to="/StartYourBrandPage" onClick={() => setIsOpen(false)}>
            Start Now
          </Link>
        </Button>
      </nav>
    </div>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <MenuProvider menu_id="16">
      <header id="main-navigation" className={cn("sticky top-0 z-50 w-full transition-all duration-500 border-b border-transparent text-foreground", isScrolled ? "bg-card/80 backdrop-blur-md border-border/40 shadow-none" : "bg-card")}>
        <div className="container mx-auto px-4 h-20 flex items-center justify-between">
          {}
          <Link to="/HomePage" className="flex-shrink-0 z-50">
            <img src="/img/watchlunch_logo.png" alt="watchlunch" className="h-7 w-auto object-contain" />
          </Link>

          {}
          <div className="hidden lg:flex flex-1 justify-center">
            <DesktopNavigation />
          </div>

          {}
          <div className="hidden lg:flex items-center gap-4">
            {}
            <Button variant="ghost" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg font-mono text-xs uppercase tracking-widest transition-all duration-300 px-6 shadow-none" asChild>
              <Link to="/StartYourBrandPage">Start Now</Link>
            </Button>
          </div>

          {}
          <Button variant="ghost" size="icon" className="lg:hidden z-50 text-foreground hover:text-foreground hover:bg-transparent" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu">
            {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </Button>

          {}
          <MobileNavigation isOpen={isMobileMenuOpen} setIsOpen={setIsMobileMenuOpen} />
        </div>
      </header>
    </MenuProvider>;
}